import React, { useEffect, useMemo, useRef } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { MetricKind } from '@/webapi/models';
import { getSeriesColor } from '@/features/details/charts/index';
import { isBrowser } from '@/utils/browser';
import { currencySymbol } from '@/utils/currencies';
import { DELETED } from '@/features/details/shared';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { breakpoints, isMobile } from '@/components/responsive';

export function Chart({
  goal,
  data,
  loading,
}: {
  goal: MetricKind;
  loading: boolean;
  data: any[];
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState, stateRef] = useDetachedState(null);

  const width = useMemo(() => {
    if (isBrowser() && ref.current) {
      if (isMobile()) {
        return (
          ref.current.parentElement.parentElement.getBoundingClientRect()
            .width * 0.94
        );
      }
      return (
        ref.current.parentElement.parentElement.getBoundingClientRect().width *
        0.95
      );
    }
    return 0;
  }, [state, ref?.current]);

  useEffect(() => {
    if (stateRef.current > 10) {
      return;
    }
    if (width === 0) {
      setTimeout(() => {
        setState((prev) => prev + 1);
      }, 500);
    }
  }, [width]);

  return (
    <ChartWrapper ref={ref} title={goal || ``}>
      <LineChart width={width} height={300}>
        <Tooltip
          formatter={formatter(goal)}
          labelStyle={{ color: `white`, fontSize: `1.3rem` }}
          itemStyle={{
            color: `white`,
            fontSize: `1.2rem`,
            fontFamily: `Inter`,
          }}
          contentStyle={{
            background: `#4D4D4D`,
            borderRadius: `1rem`,
            border: `none`,
          }}
          wrapperStyle={{
            boxShadow: `0 5px 7px 0 rgba(26,47,116,0.22)`,
          }}
        />
        <XAxis dataKey="date" type="category" allowDuplicatedCategory={false} />
        <YAxis dataKey={goal} type="number" tickFormatter={formatter(goal)} />
        <CartesianGrid stroke="#e7ebed" />
        {!loading &&
          data.map((s, i) => {
            const name = s.name || DELETED;
            return (
              <Line
                key={`${s.name}-${i}`}
                isAnimationActive={false}
                dataKey={goal}
                name={name}
                data={s.data}
                legendType="circle"
                stroke={getSeriesColor(name)}
                strokeWidth={2}
                dot={<ChartDot />}
              />
            );
          })}
      </LineChart>
    </ChartWrapper>
  );
}

export function formatter(goal: MetricKind) {
  const symbol = currencySymbol();
  if (
    [
      MetricKind.ADD_TO_CART_RATE,
      MetricKind.CTR,
      MetricKind.CHECKOUT_RATE,
      MetricKind.PRODUCT_DETAILS_VIEWS_RATE,
    ].includes(goal)
  ) {
    return (tick) => `${(tick * 100)?.toFixed(2)?.replace(`.00`, ` `)}%`;
  }
  if (isPercent(goal)) {
    return (tick) => `${tick?.toFixed(2)?.replace(`.00`, ` `)}%`;
  }
  if (isDollar(goal)) {
    return (tick) => `${symbol}${tick?.toFixed(2)?.replace(`.00`, ` `)}`;
  }
  if (isDuration(goal)) {
    return (tick) => `${tick?.toFixed(0)}s `;
  }
  return (tick) => `${tick?.toFixed(2)?.replace(`.00`, ``)}`;
}

function isDuration(goal: MetricKind) {
  return [MetricKind.AVG_SESSION_DURATION].includes(goal);
}

export function isPercent(goal: MetricKind) {
  return (
    goal?.toString()?.toLowerCase()?.includes(`rate`) ||
    goal === MetricKind.CTR ||
    goal === MetricKind.RETURNS_PER_SESSION
  );
}

export function isDollar(goal: MetricKind) {
  return [
    MetricKind.REVENUE,
    MetricKind.AVG_ORDER_VALUE,
    MetricKind.PER_SESSION_VALUE,
    MetricKind.ASSISTED_REVENUE,
    MetricKind.DIRECT_REVENUE,
    MetricKind.PROFIT,
    MetricKind.PROFIT_PER_SESSION,
    MetricKind.REVENUE_FROM_CLICKS,
  ].includes(goal);
}

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-family: Inter, serif;
  color: red;
  margin-left: -4rem;
  justify-self: center;

  ${breakpoints.down(`md`)} {
    margin-left: -2.5rem;
    margin-top: 1rem;
  }
`;

export function ChartDot(props) {
  const { cx, cy, stroke } = props;
  return (
    <svg>
      <circle
        cx={cx}
        cy={cy}
        r={7}
        stroke="#e7ebed"
        strokeWidth={1}
        fill="white"
      />
      <circle cx={cx} cy={cy} r={5} fill={stroke} />
    </svg>
  );
}
